// Safari's native customElements.define() throws NotSupportedError for `extends` (customized
// built-in elements aren't implemented there — vendor.js's polyfill is supposed to intercept
// this call before it reaches the native registry). The theme editor can also throw here on a
// section re-render ("already been used with this registry"). Either failure is otherwise
// fatal: an uncaught throw from customElements.define() aborts every statement after it in this
// file, silently killing the mega menu, dropdown nav, mobile menu drawer, and sticky header all
// at once. Wrap every registration below so one failure can't take the rest down with it.
function safeDefineElement(name, ctor, options) {
  try {
    customElements.define(name, ctor, options);
  } catch (error) {
    console.warn(`[header.js] customElements.define("${name}") failed:`, error);
  }
}

class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    // window.addEventListener('resize', this.setMenuHeight.bind(this));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;

    headerGroups.forEach((section) => {
      totalHeight += section.offsetHeight;
    });

    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);

    return totalHeight;
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      const offsetNavigationHeight = Math.round(this.headerNavigation.offsetHeight);

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-navigation-height', `${offsetNavigationHeight - 1}px`);

      this.calculateHeaderGroupHeight();
    });
  }
}

safeDefineElement('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    // Group CSS classes in one object for easier management
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    // Track navigation toggle state
    this.navigationManuallyToggled = false;

    // Initialize scroll tracking variables
    this.currentScrollTop = 0;
    this.lastScrollPos = 0;

    // Add scroll threshold to prevent quick unpinning
    this.scrollThreshold = 200; // Minimum scroll amount before unpinning
    this.scrollDirection = 'none';
    this.scrollDistance = 0;
  }

  // Getters for easier property access
  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    // Cache header dimensions for performance optimization
    this.captureStickyBounds();

    // Initialize sticky header
    this.initStickyHeader();

    // Register toggle button event if needed
    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle.bind(this));
    }

    // Market password gate (see market-password-gate.js / layout/theme.liquid): on a locked
    // market, this element sits inside #MarketPasswordGuardedContent[hidden] while the gate is
    // up. Custom elements still connect (and this connectedCallback still fires) even though
    // CSS display is none, so captureStickyBounds() above measures an all-zero DOMRect — a
    // hidden element has no layout box. That left headerBounds stuck at zero for the rest of
    // the session, so the very first scroll after unlocking mis-fired the pinned/unpinned
    // logic and the header visibly jumped instead of staying fixed. Re-measure once the real
    // content is actually revealed.
    document.addEventListener('market-password:unlocked', this.captureStickyBounds.bind(this));
  }

  // Store the scroll position and header dimensions used by the pin/unpin logic below.
  // Must be re-run any time the header goes from not-actually-visible to visible (see the
  // market-password:unlocked listener above) or the cached bounds go stale.
  captureStickyBounds() {
    this.firstScrollTop = window.scrollY;
    this.headerBounds = this.headerSection.getBoundingClientRect();
  }

  // Initialize sticky header behavior
  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  // Handle navigation toggle button click
  handleNavigationToggle(event) {
    event.preventDefault();

    this.navigationManuallyToggled = true;
    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Toggle visibility state
    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', isHidden);

    // Reset toggle state after delay
    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  // Handle scroll events
  handleScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid recalculating dimensions on each scroll
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Update scroll direction and distance
    this.updateScrollMetrics(scrollTop);

    // Use requestAnimationFrame for performance optimization
    requestAnimationFrame(() => {
      const isScrolledPastHeader = scrollTop > headerBoundsTop;

      // Handle different scroll positions
      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(scrollTop, headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }

      this.currentScrollTop = scrollTop;
    });
  }

  // Update scroll direction and accumulated distance
  updateScrollMetrics(scrollTop) {
    // Determine scroll direction
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    // Reset accumulated distance when direction changes
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      // Accumulate scroll distance in the same direction
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  // Handle scroll position after passing the header
  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    this.headerSection.classList.add(this.classes.headerScrolled);

    // Handle collapse on scroll behavior
    if (this.collapseOnScroll) {
      this.navigationToggleButton.classList.add(this.classes.show);
      if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    }

    // Handle sticky behavior based on configuration
    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      // Improved sticky on scroll up logic
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        // Only unpin when scrolled down enough and not manually toggled
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  // Handle scroll position before the header
  handleScrolledBeforeHeader() {
    this.headerSection.classList.remove(this.classes.headerScrolled);

    if (this.collapseOnScroll) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide);
        this.headerNavigation.classList.remove(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      }
      this.navigationToggleButton.classList.remove(this.classes.show);
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}

safeDefineElement('sticky-header', StickyHeader, { extends: 'header' });

// DEV ONLY: Set to false to restore normal mega menu behavior.
if (typeof window.FOX_THEME_DEV_ALWAYS_OPEN_MEGA_MENU === 'undefined') {
  window.FOX_THEME_DEV_ALWAYS_OPEN_MEGA_MENU = false;
}

const isMegaMenuDevAlwaysOpen = () =>
  window.FOX_THEME_DEV_ALWAYS_OPEN_MEGA_MENU === true && window.matchMedia('(min-width: 1024px)').matches;

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    // this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    // this.addEventListener('mouseleave', this.detectHoverListener.bind(this));

    this.addEventListener('mouseenter', (event) => {
      if (this.hoverLeaveTimer) {
        clearTimeout(this.hoverLeaveTimer);
        this.hoverLeaveTimer = null;
      }
      this.hoverEnterTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseenter' });
      }, 100);
    });

    this.addEventListener('mouseleave', (event) => {
      if (this.hoverEnterTimer) {
        clearTimeout(this.hoverEnterTimer);
        this.hoverEnterTimer = null;
      }

      this.hoverLeaveTimer = setTimeout(() => {
        this.detectHover({ type: 'mouseleave' });
      }, 150);
    });
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      // Otherwise, toggle the 'open' state
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => {
        this.contentElement.setAttribute('open', '');
      }, 100);

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      const MenuProductList = this.querySelector('menu-product-list');
      if (MenuProductList && typeof MenuProductList === 'object') {
        MenuProductList.calcNavButtonsPosition();
      }

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    if (isMegaMenuDevAlwaysOpen() && this.matches('details[is="details-mega"]')) {
      return;
    }
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (isMegaMenuDevAlwaysOpen() && this.matches('details[is="details-mega"]')) {
      return;
    }
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (isMegaMenuDevAlwaysOpen() && this.matches('details[is="details-mega"]')) {
      return;
    }
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (isMegaMenuDevAlwaysOpen() && this.matches('details[is="details-mega"]')) {
      this.open = true;
      return;
    }
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

safeDefineElement('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

// Perf: menu collection images are NOT resolved server-side. In Liquid,
// collection.featured_image falls back to a first-product query for every
// collection without its own image — ~680ms per uncached page render across
// the ~91 collection links in the header menus. Instead, the menu snippets
// ship empty [data-mm-collection] placeholders (see snippets/mega-menu.liquid
// and snippets/menu-drawer-details.liquid) and this loader fills them from
// the public /collections/<handle>/products.json endpoint when a panel is
// first opened. Fetches are cached per handle for the session.
const menuCollectionImageCache = new Map();

function loadMenuCollectionImages(scope) {
  if (!scope) return;
  const routeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const firstProductImage = (handle) => {
    if (!menuCollectionImageCache.has(handle)) {
      menuCollectionImageCache.set(
        handle,
        fetch(`${routeRoot}collections/${encodeURIComponent(handle)}/products.json?limit=1`)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            const product = data && data.products && data.products[0];
            return (product && product.images && product.images[0]) || null;
          })
          .catch(() => null)
      );
    }
    return menuCollectionImageCache.get(handle);
  };

  const sized = (src, width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;

  scope.querySelectorAll('[data-mm-collection]:not([data-mm-loaded])').forEach((wrapper) => {
    // Connox hides desktop menu images via a CSS override
    // (.menu-sidebar__subitem-toggle .blocks-radius-md { display: none } in
    // theme.css) — skip fetching for slots that can never be shown. The
    // mobile drawer's image slots don't match that selector and load normally.
    const box = wrapper.closest('.overflow-hidden');
    if (box && getComputedStyle(box).display === 'none') return;
    wrapper.setAttribute('data-mm-loaded', '');
    firstProductImage(wrapper.dataset.mmCollection).then((image) => {
      if (!image || !image.src) {
        // Empty collection (or fetch failed): collapse the placeholder so the
        // link renders text-only, as it did when Liquid resolved images.
        const box = wrapper.closest('.overflow-hidden');
        if (box) box.remove();
        return;
      }
      if (wrapper.hasAttribute('data-mm-adapt') && image.width && image.height) {
        wrapper.style.setProperty('--aspect-ratio', (image.width / image.height).toFixed(4));
      }
      // { is } upgrades the element natively (Chrome/FF); the attribute lets
      // vendor.js's customized-builtin polyfill upgrade it on insertion (Safari).
      const img = document.createElement('img', { is: 'image-lazy' });
      img.setAttribute('is', 'image-lazy');
      img.src = sized(image.src, 400);
      img.srcset = `${sized(image.src, 165)} 165w, ${sized(image.src, 360)} 360w`;
      img.sizes = '(min-width: 750px) 360px, 50vw';
      img.width = 400;
      img.height = 400;
      img.loading = 'lazy';
      img.alt = wrapper.dataset.mmAlt || '';
      img.className = 'motion-reduce hover-scale-up';
      wrapper.appendChild(img);
    });
  });
}

/**
 * Draws a scroll-position-synced fake scrollbar thumb inside `panel`, which
 * must have `overflow-y: auto|scroll`. Needed because OS-level overlay
 * scrollbars (e.g. macOS "automatically hide" trackpad setting) fade out
 * when idle and can't be forced to stay visible via native scrollbar CSS
 * (::-webkit-scrollbar/scrollbar-color) alone.
 *
 * The track lives inside a `position: sticky; top: 0; height: 0` anchor
 * placed as the panel's first child — sticky keeps it pinned to the visible
 * top of the panel's own scroll viewport (not the page), and its zero
 * height keeps it from affecting the panel's scrollHeight. The track is
 * then positioned absolutely against that sticky anchor, so it appears to
 * float over the content for as long as the panel actually overflows.
 *
 * Inserts the DOM and wires up the observers, but does NOT measure/size the
 * thumb — that first measurement is a layout-forcing read (scrollHeight/
 * clientHeight) right after a DOM write (the insertBefore above), so doing
 * it here would force a synchronous layout recalculation for every single
 * call. Returns the measure function so callers binding one panel at a time
 * can invoke it immediately, while callers binding many panels at once (see
 * MenuSidebar._bindFakeScrollbars) can insert all of them first and measure
 * them all together in one pass. Returns null if `panel` is invalid or
 * already bound.
 */
function bindFakeScrollbar(panel) {
  if (!panel || panel.dataset.fakeScrollbarBound) return null;
  panel.dataset.fakeScrollbarBound = 'true';

  const anchor = document.createElement('div');
  anchor.className = 'fake-scrollbar-anchor';
  const track = document.createElement('div');
  track.className = 'fake-scrollbar-track';
  const thumb = document.createElement('div');
  thumb.className = 'fake-scrollbar-thumb';
  track.appendChild(thumb);
  anchor.appendChild(track);
  panel.insertBefore(anchor, panel.firstChild);

  const update = () => {
    const { scrollTop, scrollHeight, clientHeight } = panel;
    const needsScroll = scrollHeight > clientHeight + 1;
    track.classList.toggle('fake-scrollbar-track--visible', needsScroll);
    if (!needsScroll) return;

    track.style.height = `${clientHeight}px`;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * 100, 10);
    const maxTop = 100 - thumbHeight;
    const top = maxTop * (scrollTop / (scrollHeight - clientHeight));
    thumb.style.height = `${thumbHeight}%`;
    thumb.style.top = `${Math.min(Math.max(top, 0), maxTop)}%`;
  };

  panel.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(panel);
  // ResizeObserver only fires when `panel`'s own box changes size. Panels
  // like .menu-sidebar__content-child are fixed-size (height/max-height come
  // from CSS) and only have their *children* swapped in/out at runtime (the
  // L3/L4 flyout gets moved in and out of them) — that changes scrollHeight
  // without ever changing the panel's own box, so ResizeObserver alone would
  // never notice and the thumb would silently go stale. Watch for child
  // mutations too so moving content in/out re-syncs it immediately.
  new MutationObserver(update).observe(panel, { childList: true });
  return update;
}

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    // Perf: the mega panel ships inside an inert <template> (see
    // snippets/mega-menu.liquid). Warm it up on first intent so the open
    // animation never waits on hydration; transition() below is the catch-all.
    if (this.contentTemplate) {
      const hydrate = this.hydrate.bind(this);
      this.addEventListener('mouseenter', hydrate, { once: true });
      this.summaryElement.addEventListener('focusin', hydrate, { once: true });
      this.summaryElement.addEventListener('touchstart', hydrate, { once: true, passive: true });
    }

    if (isMegaMenuDevAlwaysOpen()) {
      // Keep mega menu open while developing styles.
      requestAnimationFrame(() => {
        this.open = true;
      });
    }
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  get contentTemplate() {
    // Perf/SEO: the template used to be a direct child of contentElement, but
    // the real (crawlable) link columns now render eagerly ahead of it inside
    // .mega-menu__wrapper, so the template sits one level deeper — hence a
    // descendant, not child-combinator, lookup.
    return this.contentElement ? this.contentElement.querySelector('template[data-mega-content]') : null;
  }

  hydrate() {
    const template = this.contentTemplate;
    if (!template) return;
    // Reinsert at the template's own position (not appended to
    // contentElement) so hydrated content lands after the eager link columns
    // inside .mega-menu__wrapper, matching source order.
    template.replaceWith(template.content);
    // Start fetching menu images on first intent (hover/focus/touch), before
    // the panel is opened, so they're usually ready when it appears.
    loadMenuCollectionImages(this.contentElement);
    this.setupScrollbar();
  }

  /**
   * .mega-menu__container hides its native scrollbar (see theme.css) because
   * OS-level overlay scrollbars can't be forced to stay visible via CSS alone
   * on macOS trackpads — bindFakeScrollbar() draws a synced thumb instead.
   */
  setupScrollbar() {
    bindFakeScrollbar(this.contentElement.querySelector('.mega-menu__container'))?.();
  }

  async transition(value) {
    if (value) {
      this.hydrate();
      loadMenuCollectionImages(this.contentElement);
    }
    return super.transition(value);
  }

  async showWithTransition() {
    // Perform the animation on the first child of the content element
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

safeDefineElement('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick(event) {
    if (event.target.closest('a')) {
      return;
    }
    loadMenuCollectionImages(this);
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
safeDefineElement('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get header() {
    return this.closest('header');
  }

  get contentTemplate() {
    return this.querySelector(':scope > template[data-drawer-content]');
  }

  // Attribute-based rather than sniffing for a specific child element: the
  // static loading placeholder (see menu-drawer.liquid) is also a
  // .drawer__inner, so DOM shape alone can't distinguish "real content
  // hydrated" from "just showing the loading state". Set once real content
  // is actually inserted into the DOM (insertContent(), on first open — not
  // merely fetched, see hydrate()) or, for the theme editor's inline-rendered
  // content, directly in Liquid.
  get isHydrated() {
    return this.hasAttribute('data-hydrated');
  }

  // Perf: the drawer markup is not in the initial HTML on normal page loads —
  // see the comment block in snippets/menu-drawer.liquid for why, and for why
  // the 404 template is the channel this URL goes through. Liquid cannot detect
  // a Section Rendering API request, so this path must stay in sync with the
  // `request.page_type == '404'` branch in that snippet.
  get drawerFetchUrl() {
    const root = (FoxTheme.routes.root_url || '/').replace(/\/$/, '');
    return `${root}/__menu-drawer?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.isHydrated) return;

    const hydrate = () => {
      this.hydrate().catch(() => {
        /* show() retries and reports */
      });
    };

    // Perf: on a touch device, touchstart is both the "prefetch" signal and
    // the tap that opens the drawer, so there's nothing to prefetch ahead of
    // — the customer waits for the network on every open. Fetch instead once
    // the page has fully loaded and the browser has an idle moment, so the
    // content is already there by the time anyone taps. Hover-capable
    // devices keep the original on-intent prefetch below (hover already runs
    // ahead of the click there, same as before).
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const schedule = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(hydrate, { timeout: 4000 });
        } else {
          setTimeout(hydrate, 1000);
        }
      };
      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true, signal: this.abortController.signal });
      }
    }

    // Safety net for a tap that lands before the idle prefetch above has run
    // (or on hover-capable devices, the primary trigger). hydrate() is
    // memoised, so this never double-fetches.
    this.controls.forEach((button) => {
      ['pointerenter', 'touchstart', 'focus'].forEach((eventName) => {
        button.addEventListener(eventName, hydrate, {
          once: true,
          passive: true,
          signal: this.abortController.signal,
        });
      });
    });
  }

  // Ensures the content is fetched/cached and sitting inertly in a
  // <template data-drawer-content> child — NOT inserted into the live DOM.
  // This is what the load+idle prefetch and hover/focus intent call: fetching
  // ahead of time is the point, but materialising ~3,600 elements into the
  // DOM before anyone opens the menu would defeat it just the same. show()
  // is what actually inserts the content, on first open.
  hydrate() {
    // Memoised so intent-prefetch, the idle prefetch, and an immediate tap
    // can't all fetch separately. Cleared on failure so the next attempt
    // retries rather than sticking. Safe to keep resolving after the content
    // has already been inserted too — it just hands back the same template.
    if (!this._hydration) {
      this._hydration = this.ensureContentTemplate().catch((error) => {
        this._hydration = null;
        throw error;
      });
    }
    return this._hydration;
  }

  async ensureContentTemplate() {
    return this.contentTemplate || (await this.fetchContentTemplate());
  }

  // Moves the fetched template's content into the live DOM. Only called from
  // show(), on the menu's first real opening — see hydrate() above for why
  // fetching and inserting are kept separate.
  insertContent(template) {
    if (this.isHydrated || !template) return;

    // The template content contains controls (e.g. the drawer's close button)
    // that didn't exist yet when ModalComponent.connectedCallback bound the
    // [aria-controls] click listeners — bind them here, while still in the
    // fragment, so controls outside the template (overlay, hamburger) aren't
    // double-bound.
    template.content.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this), {
        signal: this.abortController.signal,
      });
    });

    // Only one .drawer__inner should exist at a time (see .drawer[active] >
    // .drawer__inner) — drop the static loading placeholder now that real
    // content is about to replace it.
    this.querySelector(':scope > .drawer__inner.menu-drawer__loading')?.remove();
    this.setAttribute('data-hydrated', '');

    this.appendChild(template.content);
    template.remove();
  }

  // Fetches the header section and lifts the drawer's <template> out of it.
  // Parsing the response into a detached <template> keeps it inert — no
  // styling, layout or image loading for the ~40% of it we discard. It also
  // beats slicing the string: the response carries other <template> tags too
  // (the desktop mega-menu's data-mega-content ones), so matching the
  // drawer's own `</template>` by hand would be guesswork.
  //
  // Only the [data-drawer-content] fragment's own innerHTML is cached in
  // sessionStorage (not the whole response, which also carries the header
  // section wrapper and CSS links — nearly double the size), keyed by the
  // fetch URL (which already carries the market/language prefix via
  // routes.root_url, and the header section id), so a later page in the same
  // session skips the network entirely. The endpoint responds without
  // cache-control headers (cf-cache-status: DYNAMIC), so this has to happen
  // on the browser side.
  async fetchContentTemplate() {
    const cacheKey = `menu-drawer-content:${this.drawerFetchUrl}`;
    let fragmentHtml = this._readSessionCache(cacheKey);

    if (fragmentHtml == null) {
      const response = await fetch(this.drawerFetchUrl);
      if (!response.ok) throw new Error(`Menu drawer fetch failed: ${response.status}`);

      const holder = document.createElement('template');
      holder.innerHTML = await response.text();

      const fetchedTemplate = holder.content.querySelector('template[data-drawer-content]');
      if (!fetchedTemplate) throw new Error('Menu drawer fetch returned no [data-drawer-content]');

      fragmentHtml = fetchedTemplate.innerHTML;
      this._writeSessionCache(cacheKey, fragmentHtml);
    } else if (!fragmentHtml) {
      // A previous write cached an empty fragment (bug, or a genuinely empty
      // section at the time) — treat as a miss so a real fetch gets another
      // chance instead of failing for the rest of the session.
      this._removeSessionCache(cacheKey);
      throw new Error('Menu drawer cached content was empty');
    }

    const template = document.createElement('template');
    template.setAttribute('data-drawer-content', '');
    template.innerHTML = fragmentHtml;

    // Same document (template came from document.createElement), so it can
    // be moved straight in and the contentTemplate getter picks it up as a
    // direct child. Still inert — this is the fetch/cache step, not insertion
    // (see insertContent()).
    this.appendChild(template);
    return template;
  }

  // sessionStorage can throw (private browsing, quota, disabled storage) —
  // treated as "no cache" rather than a hard failure in every case.
  _readSessionCache(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  _writeSessionCache(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      /* storage full/unavailable — just won't be cached across pages */
    }
  }

  _removeSessionCache(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      /* nothing to do */
    }
  }

  show(activeElement = null, animate = true) {
    if (this.open) return;
    // Perf: open immediately with the static loading placeholder (see
    // menu-drawer.liquid) instead of waiting for the fetch. hydrate() ensures
    // the content template is fetched (reusing the idle-prefetch's in-flight
    // or already-resolved promise if there is one), and insertContent() is
    // what actually materialises it into the DOM — deliberately not done
    // until this first real open, so the idle prefetch itself never inserts
    // ~3,600 elements into a page nobody asked to see the menu on.
    const opened = super.show(activeElement, animate);
    this.hydrate()
      .then((template) => this.insertContent(template))
      .catch((error) => {
        console.warn('[menu-drawer] opening without content', error);
      });
    return opened;
  }

  // get menuItems() {
  //   if (!this._menuItems) {
  //     this._menuItems = this.querySelectorAll(
  //       '.menu-drawer__menus:not(.active) > ul > li, .menu-drawer__menus:not(.active) > .menu-drawer__footer'
  //     );
  //   }
  //   return this._menuItems;
  // }

  get requiresBodyAppended() {
    return false;
  }

  prepareToShow() {
    // Hydration is awaited in show() above; by here the content is in place.
    super.prepareToShow();
    setTimeout(() => {
      // this.animateMenuItems();
    }, 300);

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  // animateMenuItems() {
  //   FoxTheme.Motion.animate(
  //     this.menuItems,
  //     {
  //       transform: ['translateX(-20px)', 'translateX(0)'],
  //       opacity: [0, 1],
  //     },
  //     {
  //       duration: 0.6,
  //       easing: [0.075, 0.82, 0.165, 1],
  //       delay: FoxTheme.Motion.stagger(0.1),
  //     }
  //   ).finished.then(() => {
  //     this.menuItems.forEach((item) => item.removeAttribute('style'));
  //   });
  // }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }
}
safeDefineElement('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.elementToFocus = this.querySelector('button');
    this._slideInitialized = false;
  }

  connectedCallback() {
    // Perf: this used to call initSlide() (Swiper instantiation + layout reads)
    // from the constructor, which ran for every product-list mega menu on every
    // page load even though the panel is closed and content-visibility:hidden.
    // With many mega menus this was a big chunk of main-thread work on load.
    // Instead, initialize the carousel the first time its mega menu is opened.
    this.megaDetails = this.closest('details[is="details-mega"]');
    if (!this.megaDetails || this.megaDetails.open) {
      this.initSlide();
      return;
    }
    this._handleMegaToggle = () => {
      if (this.megaDetails.open) this.initSlide();
    };
    this.megaDetails.addEventListener('toggle', this._handleMegaToggle);
  }

  disconnectedCallback() {
    if (this.megaDetails && this._handleMegaToggle) {
      this.megaDetails.removeEventListener('toggle', this._handleMegaToggle);
    }
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    if (this._slideInitialized) return;
    this._slideInitialized = true;
    if (this._handleMegaToggle && this.megaDetails) {
      this.megaDetails.removeEventListener('toggle', this._handleMegaToggle);
    }

    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;

    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
safeDefineElement('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
    this.handleMegaDetailsToggle = this.handleMegaDetailsToggle.bind(this);
    this.handleSubitemEnter = this.handleSubitemEnter.bind(this);
    this.handleSubitemLeave = this.handleSubitemLeave.bind(this);
    this.handleContentChildEnter = this.handleContentChildEnter.bind(this);
    this.handleContentChildLeave = this.handleContentChildLeave.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this._trackMouse = this._trackMouse.bind(this);

    // Hover-intent: track mouse position for diagonal-movement detection
    this._mousePos = { x: 0, y: 0 };
    this._prevMousePos = { x: 0, y: 0 };
    this._aimTimer = null;
    this._pendingSummary = null;

    // L3 flyout state: which subitem-content is currently reparented/open,
    // and the pending-close timer that lets the pointer travel from the
    // toggle row onto the (now relocated) flyout without it snapping shut.
    this._activeSubitemContent = null;
    this._closeSubitemTimer = null;
  }

  get summarys() {
    return this.querySelectorAll('.menu-sidebar__toggle');
  }

  get wrapper() {
    return this.closest('.mega-menu__wrapper');
  }

  get megaMenu() {
    return this.closest('.mega-menu');
  }

  connectedCallback() {
    this.megaDetails = this.closest('details[is="details-mega"]');
    if (this.megaDetails) {
      this.megaDetails.addEventListener('toggle', this.handleMegaDetailsToggle);
    }

    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', this.handleSummaryClick);
    });

    this._subItems = this.querySelectorAll('.menu-sidebar__subitem');
    this._subItems.forEach((subitem) => {
      subitem.addEventListener('mouseenter', this.handleSubitemEnter);
      subitem.addEventListener('mouseleave', this.handleSubitemLeave);
      // focusin/focusout bubble, so keyboard focus reaching the subitem's link
      // (or, once open, a link inside the relocated flyout) opens/closes it
      // the same way hover does.
      subitem.addEventListener('focusin', this.handleSubitemEnter);
      subitem.addEventListener('focusout', this.handleSubitemLeave);
    });

    this._contentChildren = this.querySelectorAll('.menu-sidebar__content-child');
    this._contentChildren.forEach((el) => {
      el.addEventListener('mouseenter', this.handleContentChildEnter);
      el.addEventListener('mouseleave', this.handleContentChildLeave);
      el.addEventListener('focusin', this.handleContentChildEnter);
      el.addEventListener('focusout', this.handleContentChildLeave);
    });

    // Hover-intent: track mouse so we can detect diagonal movement toward open panel
    this.addEventListener('mousemove', this._trackMouse);

    // Cancel any pending switch when the mouse actually reaches the content panel
    this._cancelAimOnContentEnter = () => {
      clearTimeout(this._aimTimer);
      this._aimTimer = null;
      this._pendingSummary = null;
    };
    this._l2Panels = this.querySelectorAll('.menu-sidebar__content');
    this._l2Panels.forEach((panel) => {
      panel.addEventListener('mouseenter', this._cancelAimOnContentEnter);
    });

    // Perf: refreshLayout() forces synchronous layout (getBoundingClientRect,
    // getComputedStyle) and used to run here immediately on connect, plus set up
    // a ResizeObserver + IntersectionObserver, for every sidebar mega menu on
    // every page load — even closed, content-visibility:hidden panels. That's
    // real main-thread cost multiplied by however many nav items use this menu
    // type. Defer all of it until this panel's mega menu is actually opened.
    //
    // bindFakeScrollbar() belongs in the same bucket: it also reads
    // scrollHeight/clientHeight (forced layout) via its own ResizeObserver, and
    // used to run here for every l2 panel and content-child on every page load
    // regardless of whether this menu is ever opened. Deferred alongside the
    // rest for the same reason.
    if (this.megaDetails && !this.megaDetails.open) {
      this._lazyInit = () => {
        if (this._layoutInitialized || !this.megaDetails.open) return;
        this._layoutInitialized = true;
        this._bindFakeScrollbars();
        this._setupObservers();
        this.refreshLayout();
      };
      this.megaDetails.addEventListener('toggle', this._lazyInit);
    } else {
      this._layoutInitialized = true;
      this._bindFakeScrollbars();
      this._setupObservers();
      this.refreshLayout();
    }
  }

  // Perf: bindFakeScrollbar() inserts DOM (the anchor/track/thumb) and, if
  // called eagerly, immediately follows that write with a layout-forcing
  // read to size the thumb. Doing that write-then-read once per panel, back
  // to back, forces the browser to recompute layout after every single
  // insertion — for the 12-25 panels one mega menu can have, that made the
  // first opening of a panel noticeably slower than a plain eager bind.
  // Insert all of this panel's scrollbar DOM first, then measure all of them
  // together, so layout is only recomputed once for the whole batch instead
  // of once per panel.
  _bindFakeScrollbars() {
    const panels = [...this._l2Panels, ...this._contentChildren];
    const measure = panels.map((panel) => bindFakeScrollbar(panel)).filter(Boolean);
    measure.forEach((update) => update());
  }

  _setupObservers() {
    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.refreshLayout();
        }
      });
    });
    this._intersectionObserver.observe(this);

    this._resizeObserver = new ResizeObserver(this.handleResize);
    this._resizeObserver.observe(this);

    if (this.megaMenu) {
      this._resizeObserver.observe(this.megaMenu);
    }
  }

  /** Drop hover selection when the parent mega menu closes so reopening does not “remember” the last row. */
  handleMegaDetailsToggle() {
    if (this.megaDetails && !this.megaDetails.open) {
      this.clearActiveItem();
      return;
    }

    if (this._lazyInit) this._lazyInit();
    requestAnimationFrame(() => this.refreshLayout());
  }

  handleResize() {
    if (this.megaDetails && !this.megaDetails.open) return;
    this.refreshLayout();
  }

  clearActiveItem() {
    const active = this.querySelector('.menu-sidebar__toggle.is-visible');
    if (active) {
      active.classList.remove('is-visible');
      this._setPanelActive(active, false);
    }
    const container = this.wrapper;
    if (container) {
      // container.style.removeProperty('--sidebar-height');
    }
    clearTimeout(this._closeSubitemTimer);
    this._closeSubitemTimer = null;
    this._closeActiveSubitem();
    this._clearPanelScrollState();
    this.syncMegaMenuScroll();
  }

  refreshLayout() {
    this.setInitialMinHeight();
    this.updateHeight();
  }

  getMegaMenuMaxHeight() {
    const megaMenu = this.megaMenu;
    const fallback = window.innerHeight * 0.75;
    let maxHeight = fallback;

    if (megaMenu) {
      const computedMax = parseFloat(getComputedStyle(megaMenu).maxHeight);
      maxHeight = Number.isFinite(computedMax) && computedMax > 0 ? computedMax : megaMenu.clientHeight || fallback;
    }

    // .mega-menu's own max-height (var(--mega-menu-max-height), 75vh) is
    // relative to the viewport, but .mega-menu itself starts offset from the
    // top by --header-navigation-height (see `.mega-menu { top: ... }` in
    // theme.css) — so its bottom edge can run past the actual viewport
    // bottom unless that offset also comes off the budget. Same fix as the
    // `calc(100vh - var(--header-navigation-height))` already used on
    // .menu-sidebar__content-child in theme.css.
    const headerNavHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-navigation-height')) || 0;
    maxHeight = Math.min(maxHeight, window.innerHeight - headerNavHeight);

    return Math.max(maxHeight - this._getContentBlockMargin(), 0);
  }

  /**
   * .menu-sidebar__content uses block margin (not padding) for its top/bottom
   * gap, so that gap sits outside the box instead of inside the max-height it
   * gets given when scrollable. Every height calc below clamps against
   * getMegaMenuMaxHeight(), so this margin has to come off that budget up
   * front — otherwise the panel's box height plus its own margin can add up
   * to more than the mega menu actually has room for.
   */
  _getContentBlockMargin() {
    const content = this.querySelector('.menu-sidebar__content');
    if (!content) return 0;

    const style = getComputedStyle(content);
    const marginBlock = parseFloat(style.marginBlockStart) + parseFloat(style.marginBlockEnd);
    return Number.isFinite(marginBlock) ? marginBlock : 0;
  }

  _measureToggleColumnHeight() {
    const items = this.querySelectorAll('.menu-sidebar__item');
    if (!items.length) return 0;

    const first = items[0];
    const last = items[items.length - 1];
    return Math.ceil(last.getBoundingClientRect().bottom - first.getBoundingClientRect().top);
  }

  _clampSidebarHeight(height) {
    const toggleHeight = this._measureToggleColumnHeight();
    const maxHeight = this.getMegaMenuMaxHeight();
    return Math.min(Math.max(height, toggleHeight), maxHeight);
  }

  setInitialMinHeight() {
    const container = this.wrapper;
    if (!container) return;

    const height = Math.max(this._clampSidebarHeight(this._measureToggleColumnHeight()), 1);
    container.style.setProperty('--menu-sidebar-height', `${height}px`);
    this.syncMegaMenuScroll();
  }

  syncMegaMenuScroll() {
    const megaMenu = this.megaMenu;
    if (!megaMenu) return;

    const needsScroll = megaMenu.scrollHeight > megaMenu.clientHeight + 1;
    megaMenu.classList.toggle('mega-menu--scrollable', needsScroll);
  }

  setupIntersectionObserver() {
    // Kept for backwards compatibility if referenced elsewhere; observer is created in connectedCallback.
  }

  /**
   * Measure the height of a L2 panel (`.menu-sidebar__content`) without counting
   * any L3 flyout panels inside it. L3 panels are absolutely positioned but their
   * overflow can still inflate scrollHeight on overflow:visible containers, so we
   * temporarily pull them out of layout before reading.
   */
  _measureL2Height(content) {
    const panels = content.querySelectorAll('.menu-sidebar__subitem-content');
    panels.forEach((el) => (el.style.display = 'none'));
    const h = content.scrollHeight;
    panels.forEach((el) => (el.style.display = ''));
    return h;
  }

  _clearPanelScrollState() {
    this.querySelectorAll('.menu-sidebar__content--scrollable').forEach((el) => {
      el.classList.remove('menu-sidebar__content--scrollable');
      el.style.removeProperty('--sidebar-panel-max-height');
    });
    this.querySelectorAll('.menu-sidebar__subitem-content--scrollable').forEach((el) => {
      el.classList.remove('menu-sidebar__subitem-content--scrollable');
      el.style.removeProperty('--sidebar-panel-max-height');
    });
  }

  _syncL2ScrollState(content, l2Height) {
    this.querySelectorAll('.menu-sidebar__content--scrollable').forEach((el) => {
      if (el !== content) {
        el.classList.remove('menu-sidebar__content--scrollable');
        el.style.removeProperty('--sidebar-panel-max-height');
      }
    });

    if (!content) return;

    const panelMax = this.getMegaMenuMaxHeight();
    if (l2Height > panelMax) {
      content.classList.add('menu-sidebar__content--scrollable');
      content.style.setProperty('--sidebar-panel-max-height', `${panelMax}px`);
    } else {
      content.classList.remove('menu-sidebar__content--scrollable');
      content.style.removeProperty('--sidebar-panel-max-height');
    }
  }

  _syncL3ScrollState(subitemContent, l3Height) {
    this.querySelectorAll('.menu-sidebar__subitem-content--scrollable').forEach((el) => {
      if (el !== subitemContent) {
        el.classList.remove('menu-sidebar__subitem-content--scrollable');
        el.style.removeProperty('--sidebar-panel-max-height');
        el.closest('.menu-sidebar__content-child')?.style.removeProperty('--sidebar-panel-max-height');
      }
    });

    if (!subitemContent) return;

    const panelMax = this.getMegaMenuMaxHeight();
    if (l3Height > panelMax) {
      subitemContent.classList.add('menu-sidebar__subitem-content--scrollable');
      subitemContent.style.setProperty('--sidebar-panel-max-height', `${panelMax}px`);
    } else {
      subitemContent.classList.remove('menu-sidebar__subitem-content--scrollable');
      subitemContent.style.removeProperty('--sidebar-panel-max-height');
    }
    // .menu-sidebar__content-child wraps subitemContent, but its own
    // max-height falls back to the static --mega-menu-max-height var (see
    // theme.css) — it never learns the real, viewport-aware panelMax used
    // above. On short viewports that static fallback can be taller than
    // what's actually available, letting content push past the visible menu
    // instead of triggering the content-child's own fake scrollbar. Give it
    // the same cap regardless of whether subitemContent itself needed to
    // scroll, so the two stay consistent.
    subitemContent.closest('.menu-sidebar__content-child')?.style.setProperty('--sidebar-panel-max-height', `${panelMax}px`);
  }

  updateHeight() {
    const container = this.wrapper;
    if (!container) return;

    const activeSummary = this.querySelector('.is-visible');
    if (!activeSummary) {
      // container.style.removeProperty('--sidebar-height');
      this._clearPanelScrollState();
      this.setInitialMinHeight();
      return;
    }

    const content = activeSummary.nextElementSibling;
    if (!content) return;

    const l2Height = this._measureL2Height(content);
    const sidebarHeight = this._clampSidebarHeight(l2Height);
    // container.style.setProperty('--sidebar-height', `${sidebarHeight}px`);
    this._syncL2ScrollState(content, l2Height);
    this._syncL3ScrollState(null, 0);
    this.syncMegaMenuScroll();
  }

  // The L2 panel's visibility is keyed off its own class rather than a
  // `.is-visible + .menu-sidebar__content` sibling selector — Firefox doesn't
  // reliably restyle a <details> child when its <summary> sibling's class
  // changes (see .menu-sidebar__content--active in theme.css).
  _setPanelActive(summaryElem, active) {
    const content = summaryElem.nextElementSibling;
    if (content && content.classList.contains('menu-sidebar__content')) {
      content.classList.toggle('menu-sidebar__content--active', active);
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
      this._setPanelActive(lastSidenavElem, false);
    }

    summaryElem.classList.add('is-visible');
    this._setPanelActive(summaryElem, true);

    if (updateHeight) {
      this.updateHeight();
    }
  }

  _trackMouse(evt) {
    this._prevMousePos = { x: this._mousePos.x, y: this._mousePos.y };
    this._mousePos = { x: evt.clientX, y: evt.clientY };
  }

  /**
   * Returns true when the mouse appears to be moving diagonally toward the
   * currently-open L2 content panel (i.e. the user is "aiming" at it).
   * Uses the classic slope-triangle technique: project the mouse vector onto
   * the content panel's left edge and check whether it lands inside the panel.
   */
  _isPossiblyAimingAtContent() {
    const currentActive = this.querySelector('.is-visible');
    if (!currentActive) return false;

    const content = currentActive.nextElementSibling;
    if (!content) return false;

    const rect = content.getBoundingClientRect();
    const mouse = this._mousePos;
    const prev = this._prevMousePos;

    const dx = mouse.x - prev.x;
    // Only care when the mouse is actually moving toward the panel (rightward)
    if (dx <= 0) return false;

    const dy = mouse.y - prev.y;
    const distToPanel = rect.left - mouse.x;
    if (distToPanel <= 0) return false; // Already past the panel edge

    const t = distToPanel / dx;
    const projectedY = mouse.y + t * dy;

    // Add a small tolerance so the triangle isn't razor-thin
    const tolerance = 8;
    return projectedY >= rect.top - tolerance && projectedY <= rect.bottom + tolerance;
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.currentTarget;
    const currentActive = this.querySelector('.is-visible');

    // No active item or same item → switch immediately
    if (!currentActive || currentActive === summaryElem) {
      clearTimeout(this._aimTimer);
      this._aimTimer = null;
      this._pendingSummary = null;
      this.setActiveItem(summaryElem);
      return;
    }

    if (this._isPossiblyAimingAtContent()) {
      // Mouse is heading toward the open panel — delay the switch so diagonal
      // movement doesn't accidentally change the active parent category.
      clearTimeout(this._aimTimer);
      this._pendingSummary = summaryElem;
      this._aimTimer = setTimeout(() => {
        if (this._pendingSummary === summaryElem) {
          this.setActiveItem(summaryElem);
        }
        this._aimTimer = null;
        this._pendingSummary = null;
      }, 300);
    } else {
      clearTimeout(this._aimTimer);
      this._aimTimer = null;
      this._pendingSummary = null;
      this.setActiveItem(summaryElem);
    }
  }

  handleSummaryClick(e) {
    if (e.target.closest('a[href]')) {
      return;
    }
    e.preventDefault();
  }

  /**
   * SEO: the L4 flyout is real DOM (see mega-menu.liquid), not <template>, so
   * it's always the same live node — just reparented in and out of
   * .menu-sidebar__content-child on hover/focus/close, never cloned.
   */
  _openSubitemContent(subitem) {
    if (subitem._liveSubitemContent) return subitem._liveSubitemContent;

    const subitemContent = subitem.querySelector(':scope > .menu-sidebar__subitem-content');

    if (subitemContent) bindFakeScrollbar(subitemContent)?.();
    subitem._liveSubitemContent = subitemContent || null;
    return subitem._liveSubitemContent;
  }

  /**
   * .menu-sidebar__content is width-capped with a scrollbar (see theme.css),
   * which clips the L3 flyout even though it's absolutely positioned —
   * overflow clips descendants regardless of position. So while a flyout is
   * open it's physically moved into the unclipped sibling
   * .menu-sidebar__content-child, matched to sit in the exact same box.
   */
  _moveSubitemContentToChild(subitem, subitemContent) {
    const contentChild = subitem.closest('.menu-sidebar__item')?.querySelector(':scope > .menu-sidebar__content-child');
    if (!contentChild) return;
    // .menu-sidebar__content-child--has-flyout drives the pointer-events toggle
    // in theme.css (see comment there). It used to be `:not(:empty)`, but
    // bindFakeScrollbar() permanently seeds a .fake-scrollbar-anchor child
    // into every content-child at connect time, so the element is never
    // truly empty anymore and that selector always matched.
    contentChild.classList.add('menu-sidebar__content-child--has-flyout');
    if (subitemContent.parentElement === contentChild) return;

    contentChild.appendChild(subitemContent);
  }

  _closeActiveSubitem() {
    if (!this._activeSubitemContent) return;
    const subitemContent = this._activeSubitemContent;
    const subitem = this._activeSubitem;
    subitemContent.classList.remove('is-open');

    if (subitem) {
      subitem.appendChild(subitemContent);
    }

    const contentChild = subitem?.closest('.menu-sidebar__item')?.querySelector(':scope > .menu-sidebar__content-child');
    if (contentChild) {
      contentChild.classList.remove('menu-sidebar__content-child--has-flyout');
      contentChild.style.removeProperty('--sidebar-panel-max-height');
    }

    this._activeSubitemContent = null;
    if (subitem) {
      subitem.classList.remove('is-active');
    }
    this._activeSubitem = null;
    this._syncL3ScrollState(null, 0);
  }

  /**
   * When the pointer enters a L2 item that has a L3 flyout, expand the sidebar
   * height to whichever is taller: the L2 panel or the L3 panel (capped to the mega menu),
   * and open the flyout (reparenting it out of the clipped L2 panel).
   */
  handleSubitemEnter(evt) {
    const subitem = evt.currentTarget;
    const subitemContent = this._openSubitemContent(subitem);
    if (!subitemContent) return;

    clearTimeout(this._closeSubitemTimer);
    this._closeSubitemTimer = null;

    if (this._activeSubitemContent && this._activeSubitemContent !== subitemContent) {
      this._closeActiveSubitem();
    }

    const container = this.wrapper;
    const activeSummary = this.querySelector('.is-visible');
    if (!container || !activeSummary) return;

    const l2Panel = activeSummary.nextElementSibling;
    if (!l2Panel) return;

    this._moveSubitemContentToChild(subitem, subitemContent);
    subitemContent.classList.add('is-open');
    this._activeSubitemContent = subitemContent;
    subitem.classList.add('is-active');
    this._activeSubitem = subitem;

    const l2Height = this._measureL2Height(l2Panel);
    const l3Height = subitemContent.scrollHeight;
    const sidebarHeight = this._clampSidebarHeight(Math.max(l2Height, l3Height));

    // container.style.setProperty('--sidebar-height', `${sidebarHeight}px`);
    this._syncL2ScrollState(l2Panel, l2Height);
    this._syncL3ScrollState(subitemContent, l3Height);
    this.syncMegaMenuScroll();
  }

  /**
   * When the pointer leaves a L2 item, close the flyout — but only after a
   * short delay, since the flyout now lives in a DOM location outside the
   * subitem (.menu-sidebar__content-child), so a mouseleave fires here as
   * soon as the pointer crosses into it. handleContentChildEnter cancels this
   * timer if the pointer actually lands on the flyout.
   */
  handleSubitemLeave() {
    clearTimeout(this._closeSubitemTimer);
    this._closeSubitemTimer = setTimeout(() => {
      this._closeSubitemTimer = null;
      this._closeActiveSubitem();
      this.updateHeight();
    }, 100);
  }

  /** Pointer/focus reached the relocated flyout — cancel the pending close from handleSubitemLeave. */
  handleContentChildEnter() {
    clearTimeout(this._closeSubitemTimer);
    this._closeSubitemTimer = null;
  }

  /** Pointer/focus left the relocated flyout without returning to the subitem — close it. */
  handleContentChildLeave() {
    this.handleSubitemLeave();
  }

  disconnectedCallback() {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this.megaDetails) {
      this.megaDetails.removeEventListener('toggle', this.handleMegaDetailsToggle);
      if (this._lazyInit) {
        this.megaDetails.removeEventListener('toggle', this._lazyInit);
      }
      this.megaDetails = null;
    }
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleSummaryClick);
    });
    if (this._subItems) {
      this._subItems.forEach((subitem) => {
        subitem.removeEventListener('mouseenter', this.handleSubitemEnter);
        subitem.removeEventListener('mouseleave', this.handleSubitemLeave);
      });
    }
    this.removeEventListener('mousemove', this._trackMouse);
    if (this._cancelAimOnContentEnter) {
      this.querySelectorAll('.menu-sidebar__content').forEach((panel) => {
        panel.removeEventListener('mouseenter', this._cancelAimOnContentEnter);
      });
    }
    clearTimeout(this._aimTimer);
    this._aimTimer = null;
    this._pendingSummary = null;
  }
}

safeDefineElement('menu-sidebar', MenuSidebar);
